import api, { type ApiRequestConfig } from "@/lib/axios";

/**
 * MedDRA verbatim search, for the coding control on a product's indication.
 *
 * <p>The endpoint is `GET /medDRA_codes_for_verbatim` on safety-db-service, which is legacy Python
 * and has no generated client — the one place this feature reaches a service without one. The
 * service target is named on the request rather than left to the axios instance's fallback, so
 * which service this talks to is readable here instead of being decided elsewhere by omission.
 *
 * <p>It does not swallow failures. A lookup that fails and a lookup that matched nothing are
 * different answers, and a clinical coding control that shows the first as the second invites a
 * reviewer to conclude a term does not exist when nobody asked. The caller renders them apart.
 *
 * <p>Retirement: this moves to a case-db MedDRA search when Java has one; nothing else in the
 * catalog feature calls safety-db.
 */

/** The minimum the endpoint accepts; shorter input is not a search. */
export const MEDDRA_MIN_QUERY = 3;

/** One MedDRA match, reduced to the two terms the catalog stores. */
export interface MedDraMatch {
  preferredTerm: string;
  lowerLevelTerm: string;
}

/** The shape the legacy endpoint answers with, named here because no generated client names it. */
interface MedDraResponse {
  data?: { codedEvents?: Array<{
    preferredTerm?: { term?: string };
    lowerLevelTerm?: { term?: string };
  }> };
}

export async function searchMedDraTerms(verbatim: string, signal?: AbortSignal): Promise<MedDraMatch[]> {
  const term = verbatim.trim();
  if (term.length < MEDDRA_MIN_QUERY) {
    return [];
  }
  const request: ApiRequestConfig = {
    service: "safety",
    params: { reported_term: term },
    // The control searches as the reviewer types; a page-level spinner per keystroke is noise.
    headers: { "X-Skip-Loading": "true" },
    signal,
  };
  const response = await api.get("/medDRA_codes_for_verbatim", request);
  const body = response.data as MedDraResponse | undefined;
  return (body?.data?.codedEvents ?? []).flatMap((coded) => {
    const preferredTerm = coded.preferredTerm?.term?.trim();
    const lowerLevelTerm = coded.lowerLevelTerm?.term?.trim();
    return preferredTerm ? [{ preferredTerm, lowerLevelTerm: lowerLevelTerm ?? preferredTerm }] : [];
  });
}
